import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '../config/api';
import { FaHeart, FaEye, FaTag, FaUser, FaCalendar, FaShare, FaFilter, FaSort } from 'react-icons/fa';
import { Link } from 'react-router-dom';

interface SharedLearningPost {
  id: string;
  userId: string;
  username: string;
  userProfilePicture?: string;
  title: string;
  description: string;
  progressData: {
    totalWordsLearned: number;
    currentStreak: number;
    totalStudyTime: number;
    averageAccuracy: number;
    level: number;
    xpPoints: number;
  };
  achievements: Array<{
    title: string;
    description: string;
    icon: string;
    unlockedAt: string;
  }>;
  recentActivity: Array<{
    date: string;
    wordsLearned: number;
    timeSpent: number;
    accuracy: number;
  }>;
  categoryProgress: Array<{
    category: string;
    totalWords: number;
    masteredWords: number;
    percentage: number;
  }>;
  tags: string[];
  likes: number;
  views: number;
  createdAt: string;
}

interface SharedLearningResponse {
  data: SharedLearningPost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const SharedLearningPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'createdAt' | 'likes' | 'views' | 'progressData.level'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch shared learning posts
  const { data: sharedLearningData, isLoading, error } = useQuery<SharedLearningResponse>({
    queryKey: ['sharedLearning', currentPage, sortBy, sortOrder, selectedTags, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '12',
        sortBy: sortBy,
        order: sortOrder,
        ...(selectedTags.length > 0 && { tags: selectedTags.join(',') }),
        ...(searchQuery && { search: searchQuery })
      });

      const response = await fetch(`${API_BASE_URL}/api/shared-learning/public?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch shared learning posts');
      }
      return response.json();
    }
  });

  const handleLike = async (postId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shared-learning/${postId}/like`, {
        method: 'POST'
      });
      if (response.ok) {
        // Refetch the data to update likes
        window.location.reload();
      }
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag) 
        : [...prev, tag]
    );
    setCurrentPage(1);
  };

  const handleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setSearchQuery('');
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading shared learning posts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Error Loading Posts</h2>
          <p className="text-gray-600 dark:text-gray-400">Failed to load shared learning posts. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Shared Learning Stories 📚
            <div className="text-xl font-normal text-gray-600 dark:text-gray-400 mt-2">
              داستان‌های یادگیری اشتراک‌گذاری شده
            </div>
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Discover how others are learning Persian and get inspired by their progress, achievements, and learning strategies.
          </p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search Posts
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title or description..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sort By
              </label>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field as typeof sortBy);
                  setSortOrder(order as 'asc' | 'desc');
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="createdAt-desc">Newest First</option>
                <option value="createdAt-asc">Oldest First</option>
                <option value="likes-desc">Most Liked</option>
                <option value="views-desc">Most Viewed</option>
                <option value="progressData.level-desc">Highest Level</option>
              </select>
            </div>

            {/* Popular Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Popular Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {['beginner', 'intermediate', 'advanced', 'grammar', 'vocabulary', 'culture'].map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleTagToggle(tag)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedTags.includes(tag)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Posts Grid */}
        {sharedLearningData?.data && sharedLearningData.data.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {sharedLearningData.data.map((post) => (
              <div key={post.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                {/* Post Header */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-3">
                      {post.userProfilePicture ? (
                        <img 
                          src={post.userProfilePicture} 
                          alt={post.username}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        post.username.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{post.username}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Level {post.progressData.level} • {post.progressData.xpPoints} XP
                      </p>
                    </div>
                  </div>
                  
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{post.title}</h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3">{post.description}</p>
                </div>

                {/* Progress Stats */}
                <div className="p-6 bg-gray-50 dark:bg-gray-700">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{post.progressData.totalWordsLearned}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Words Learned</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{post.progressData.currentStreak}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Day Streak</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Study Time</span>
                      <span className="font-medium">{post.progressData.totalStudyTime} min</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Accuracy</span>
                      <span className="font-medium">{post.progressData.averageAccuracy}%</span>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {post.tags.length > 0 && (
                  <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex flex-wrap gap-2">
                      {post.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                          <FaTag className="inline mr-1" />
                          {tag}
                        </span>
                      ))}
                      {post.tags.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs">
                          +{post.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Post Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <button
                        onClick={() => handleLike(post.id)}
                        className="flex items-center hover:text-red-500 transition-colors"
                      >
                        <FaHeart className="mr-1" />
                        {post.likes}
                      </button>
                      <span className="flex items-center">
                        <FaEye className="mr-1" />
                        {post.views}
                      </span>
                    </div>
                    <Link
                      to={`/shared-learning/${post.id}`}
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                    >
                      View Details →
                    </Link>
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    <FaCalendar className="inline mr-1" />
                    {new Date(post.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📚</div>
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">No Posts Found</h3>
            <p className="text-gray-500 dark:text-gray-500">
              No shared learning posts match your current filters. Try adjusting your search criteria.
            </p>
          </div>
        )}

        {/* Pagination */}
        {sharedLearningData?.pagination && sharedLearningData.pagination.totalPages > 1 && (
          <div className="flex justify-center">
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              
              {Array.from({ length: sharedLearningData.pagination.totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-4 py-2 rounded-lg ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, sharedLearningData.pagination.totalPages))}
                disabled={currentPage === sharedLearningData.pagination.totalPages}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedLearningPage;
